import { DateTime } from 'luxon';
import usStates, { US_TERRITORY_ABBREVIATIONS } from '../../lib/usStates';
import { Chip, Device } from './types';

/** Em dash used as a placeholder for missing values. */
const EM_DASH = '—';

/**
 * Whether a fostered pet has the foster-household contact info the one-click
 * foster→adoption flow needs (first name, email, phone) to invite the adopter.
 * Shared by the Adopt button's disabled state and the transfer handler's guard
 * so the two can't drift.
 */
export function isFosterContactComplete(device: Device): boolean {
  // Trimmed: a whitespace-only field is not a contact. Untrimmed, the recall modal offered to save a
  // home the backend then resolved no snapshot for, and the Adopt button enabled a transfer with
  // nowhere to send the adopter's email.
  return !!device.holderFirstName?.trim() && !!device.holderEmail?.trim() && !!device.holderPhone?.trim();
}

/** Pragmatic email shape check — backend remains authoritative. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * How long a search box waits before the portal asks the server. Shared by the Devices and
 * Dashboard pages: both now filter in Postgres, so a keystroke is a request rather than a
 * re-slice of a list already in hand.
 */
export const SEARCH_DEBOUNCE_MS = 300;

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * Format a date string (YYYY-MM-DD) into a human-readable format.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return EM_DASH;
  }
  const d = DateTime.fromISO(dateStr);
  if (!d.isValid) {
    return EM_DASH;
  }
  return d.toFormat('LLL d, yyyy');
}

/**
 * Get display name for a chip's adopter/owner.
 */
export function getAdopterDisplayName(chip: Chip): string {
  if (chip.assignedTo === 'rescue') {
    return 'Rescue (in care)';
  }
  if (chip.adopterFirstName && chip.adopterLastName) {
    return `${chip.adopterFirstName} ${chip.adopterLastName}`;
  }
  return EM_DASH;
}

/**
 * Format an ISO timestamp into a compact relative string (e.g. "Now", "5 minutes ago", "3 hours ago", "2 days ago").
 * Uses luxon's toRelative() for proper locale-aware relative formatting.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) {
    return EM_DASH;
  }
  const dt = DateTime.fromISO(ts);
  if (!dt.isValid) {
    return EM_DASH;
  }
  const diffMins = DateTime.now().diff(dt, 'minutes').minutes;
  if (diffMins < 2) {
    return 'Now';
  }
  return dt.toRelative() ?? EM_DASH;
}

/**
 * Format a date string into a relative "X hours/days ago" string (day-level granularity).
 * Uses luxon's toRelative() for proper locale-aware relative formatting.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatDaysAgo(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return EM_DASH;
  }
  const dt = DateTime.fromISO(dateStr);
  if (!dt.isValid) {
    return EM_DASH;
  }
  return dt.toRelative() ?? EM_DASH;
}

/**
 * Strip a phone number down to its digits for lenient comparison. Approximates
 * the backend's `normalizePhoneNumber` closely enough that two numbers entered
 * with different formatting (e.g. "(555) 555-0100" vs "+1 555-555-0100") will
 * compare equal at the digit level. An 11-digit result starting with `1` is
 * treated as a US country-code variant of the trailing 10 digits and stripped
 * to match the bare-10-digit form.
 */
export function normalizePhoneDigits(phone: string | null | undefined): string {
  const digits = (phone ?? '').replace(/\D+/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
}

/**
 * Enough of a phone number to submit: 10 digits for a US number, the backend judges the rest. Both
 * foster-contact writes (`rescueCreateFosterHome` and `rescueCreatePetFosterTransfer`) require one,
 * and the reuse matcher keys on it, so the two forms must not disagree on what counts as complete.
 */
export function isPhoneComplete(phone: string | null | undefined): boolean {
  return normalizePhoneDigits(phone).length >= 10;
}

/**
 * Format a phone number for display as `(555) 555-0100`. Falls back to the input
 * unchanged when it isn't a recognizable 10-digit US number, so international or
 * partial numbers stored on a foster contact still render as entered rather than
 * being mangled into a US shape they don't fit.
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  const digits = normalizePhoneDigits(phone);
  if (digits.length !== 10) {
    return (phone ?? '').trim();
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * States, DC, and US territories, for the order and account address forms —
 * the surfaces that ship hardware and so need PR and VI. Dropdowns show
 * `name` and submit `abbreviation`.
 */
export const US_STATE_OPTIONS = usStates;

/**
 * US state abbreviations (50 states + DC, no territories) for the address
 * forms that collect adopter and foster home addresses.
 */
export const US_STATES = usStates
  .filter((option) => !US_TERRITORY_ABBREVIATIONS.includes(option.abbreviation))
  .map((option) => option.abbreviation);

// ── Unit conversion ──────────────────────────

export const KG_TO_LBS = 2.20462;

export function kgToLbs(kg: number | null | undefined): number {
  if (kg == null || kg === 0) {
    return 0;
  }
  return Math.round(kg * KG_TO_LBS * 10) / 10;
}

export function lbsToKg(lbs: number | null | undefined): number {
  if (lbs == null || lbs === 0) {
    return 0;
  }
  return Math.round((lbs / KG_TO_LBS) * 10) / 10;
}

// ── Pet status helpers ──────────────────────────

export type PetStatus = 'rescue' | 'foster' | 'pending_adoption' | 'adopted';

export const STATUS_LABELS: Record<PetStatus, string> = {
  rescue: 'Rescue',
  foster: 'Fostered',
  pending_adoption: 'Pending Adoption',
  adopted: 'Adopted',
};

export const STATUS_COLORS: Record<PetStatus, string> = {
  rescue: 'grey',
  foster: 'blue',
  pending_adoption: 'amber',
  adopted: 'green',
};

/**
 * The Devices page tab that lists a pet's device, keyed by pet status. Placements and adoptions are
 * split out of the Devices tab — its Activated section is backed by the `IN_RESCUE` filter, which
 * excludes both — so null means "the default tab already shows it".
 */
const DEVICE_TAB_BY_PET_STATUS: Record<PetStatus, 'fosters' | 'adoptions' | null> = {
  rescue: null,
  foster: 'fosters',
  pending_adoption: 'adoptions',
  adopted: 'adoptions',
};

/**
 * Link to a pet's device on the Devices page, pointing at the tab that actually shows the row.
 */
export function petDeviceLink(moduleId: string, status?: PetStatus | null): string {
  const tab = status ? DEVICE_TAB_BY_PET_STATUS[status] : null;
  const tabParam = tab ? `&tab=${tab}` : '';
  return `/rescue/devices?search=${encodeURIComponent(moduleId)}${tabParam}`;
}

export function speciesEmoji(species?: string | null): string {
  if (!species) {
    return '🐾';
  }
  const s = species.toUpperCase();
  if (s === 'DOG') {
    return '🐶';
  }
  if (s === 'CAT') {
    return '🐱';
  }
  return '🐾';
}
